// frontend/src/utils/roleUtils.js

/**
 * Role hierarchy utilities for role-based access control
 * Future-proofed to support roles above super_admin
 */

// Define role hierarchy from lowest to highest
export const ROLE_HIERARCHY = [
  'user',
  'driver', 
  'driver_admin',
  'admin',
  'power_admin',
  'super_admin',
  'sys_admin', // Future role
  'root_admin' // Future role
];

/**
 * Get the numeric level of a role in the hierarchy
 * @param {string} role - The role to check
 * @returns {number} - The role level (higher number = higher role)
 */
export const getRoleLevel = (role) => {
  const index = ROLE_HIERARCHY.indexOf(role);
  return index === -1 ? -1 : index;
};

/**
 * Check if a user has a role at or above the required level
 * @param {string} userRole - The user's current role
 * @param {string} requiredRole - The minimum required role
 * @returns {boolean} - True if user has sufficient role level
 */
export const hasRoleLevel = (userRole, requiredRole) => {
  const userLevel = getRoleLevel(userRole);
  const requiredLevel = getRoleLevel(requiredRole);
  
  if (userLevel === -1 || requiredLevel === -1) {
    return false;
  }
  
  return userLevel >= requiredLevel;
};

/**
 * Check if user is super admin or above (system administration level)
 * @param {string} userRole - The user's current role
 * @returns {boolean} - True if user can access system administration
 */
export const isSystemAdmin = (userRole) => {
  return hasRoleLevel(userRole, 'super_admin');
};

/**
 * Check if user is admin or above (entity administration level)  
 * @param {string} userRole - The user's current role
 * @returns {boolean} - True if user can access admin features
 */
export const isAdmin = (userRole) => {
  return hasRoleLevel(userRole, 'admin');
};

/**
 * Check if user is driver or above
 * @param {string} userRole - The user's current role
 * @returns {boolean} - True if user can access driver features
 */
export const isDriver = (userRole) => {
  return hasRoleLevel(userRole, 'driver');
};

/**
 * Get all roles at or above a certain level
 * @param {string} minimumRole - The minimum role level
 * @returns {string[]} - Array of roles at or above the minimum level
 */
export const getRolesAtOrAbove = (minimumRole) => {
  const minimumLevel = getRoleLevel(minimumRole);
  if (minimumLevel === -1) return [];
  
  return ROLE_HIERARCHY.slice(minimumLevel);
};

/**
 * Get all roles at or below a certain level (same level and lower)
 * @param {string} userRole - The user's current role
 * @returns {string[]} - Array of roles at or below the user's level
 */
export const getRolesAtOrBelow = (userRole) => {
  const userLevel = getRoleLevel(userRole);
  if (userLevel === -1) return [];
  
  return ROLE_HIERARCHY.slice(0, userLevel + 1);
};

/**
 * Role values a user can assign, ordered from highest visible privilege down.
 * Unknown current roles fail closed to ordinary user assignment so UI callers
 * do not accidentally expose privileged values when role metadata is missing.
 * @param {string} userRole - The current user's role
 * @returns {string[]} Assignable role values
 */
export const getAssignableRoleValues = (userRole) => {
  const assignableRoles = getRolesAtOrBelow(userRole);

  if (assignableRoles.length === 0) {
    return ['user'];
  }

  return [...assignableRoles].reverse();
};

/**
 * Get display name for a role
 * @param {string} role - The role identifier
 * @returns {string} - Human-readable role name
 */
export const getRoleDisplayName = (role) => {
  const roleNames = {
    'user': 'user',
    'driver': 'driver', 
    'driver_admin': 'driver_admin',
    'admin': 'admin',
    'power_admin': 'power_admin',
    'super_admin': 'super_admin',
    'sys_admin': 'sys_admin',
    'root_admin': 'root_admin'
  };
  
  return roleNames[role] || role;
};

/**
 * Get roles that can be assigned by the current user (same level or below)
 * @param {string} userRole - The user's current role
 * @returns {Array} - Array of {value, label} objects for dropdowns
 */
export const getAssignableRoles = (userRole) => {
  const allowedRoles = getAssignableRoleValues(userRole);
  
  return allowedRoles.map(role => ({
    value: role,
    label: getRoleDisplayName(role)
  }));
};

export default {
  ROLE_HIERARCHY,
  getRoleLevel,
  hasRoleLevel,
  isSystemAdmin,
  isAdmin,
  isDriver,
  getRolesAtOrAbove,
  getRolesAtOrBelow,
  getAssignableRoleValues,
  getRoleDisplayName,
  getAssignableRoles
};
